import React from 'react';
import { get } from 'lodash';
import './UiWidgetHelpers.scss';

export const removeBracketsFromTag = (tag: string) => {
	return tag?.replace(/^[{]{2}|[}]{2}$/g, '') || '';
};

/**
 * Enhanced get function that supports array expansion with [] notation
 * Example: "Delivery addresses.value[].code" will extract all "code" values from the array
 * @param obj The object to get values from
 * @param path The path with optional [] for array expansion
 * @param defaultValue The default value if path is not found
 * @returns The value or array of values if [] notation is used
 */
function getWithArrayExpansion(obj: any, path: string, defaultValue: any = ''): any {
	// Check if path contains [] notation
	if (!path.includes('[]')) {
		return get(obj, path, defaultValue);
	}

	// Split the path at [] to get the path before and after array expansion
	// "Delivery addresses.value[].code" -> ["Delivery addresses.value", ".code"]
	const [beforeArray, afterArray] = path.split('[]');

	// Get the array using the path before []
	const arrayValue = get(obj, beforeArray, null);

	if (!Array.isArray(arrayValue)) {
		return defaultValue;
	}

	const typedArray: any[] = arrayValue;

	// If there's no path after [], return the array itself
	const remainingPath = afterArray.startsWith('.') ? afterArray.slice(1) : afterArray;
	if (!remainingPath) {
		return typedArray;
	}

	// Extract the remaining path from each array item
	return typedArray
		.map((item: any) => `"${get(item, remainingPath, defaultValue)}"`)
		.filter((val: string) => val !== `"${defaultValue}"`);
}

const processTags = (
	text?: string,
	dataSource?: Record<string, any>,
	settings?: Record<string, any>,
	allMustMatch?: boolean
): string | null => {
	if (!text) return '';
	const hasTagData = !!dataSource || !!settings;
	if (!hasTagData) return text;
	const matches = text.match(/"?\{\{(.*?)\}\}"?/g);
	if (!matches) return text;

	const matchesFound = 0;

	const textResult = matches.reduce((acc: string, match: string) => {
		const matchHasQuotes = match.startsWith('"') && match.endsWith('"');
		const key = match.replace(/"?{{/g, '').replace(/}}"?/g, '');
		let value = '';

		// Handle special case for custom fields accessed by name
		// Pattern: order.client.custom['Field Name'] or order.client.custom["Field Name"]
		// Also supports: order.client.custom.['Field Name'] (with dot before bracket)
		// And property access: order.client.custom['Field Name'].id
		const customFieldMatch = key.match(/^(.+\.custom)\.?\[(?:'|")(.+?)(?:'|")\](.*)$/);
		if (customFieldMatch && dataSource) {
			const basePath = customFieldMatch[1];
			const fieldName = customFieldMatch[2];
			const propertyPath = customFieldMatch[3]; // Captures .id, .default, etc.
			const customFields = get(dataSource, basePath) as any[];

			// First, try to find the field object
			let fieldObject = null;
			if (Array.isArray(customFields)) {
				fieldObject = customFields.find(f => f.name === fieldName);
			}

			// If not found in .custom, try .$mappedCustom
			if (!fieldObject) {
				const mappedCustomPath = basePath.replace('.custom', '.$mappedCustom');
				const mappedCustomFields = get(dataSource, mappedCustomPath) as Record<string, any>;

				if (mappedCustomFields && typeof mappedCustomFields === 'object') {
					fieldObject = Object.values(mappedCustomFields).find((field: any) => field?.name === fieldName);
				}
			}

			// If we found the field object, extract the requested property
			if (fieldObject) {
				if (propertyPath && propertyPath.startsWith('.')) {
					// Access a specific property like .id, .default, etc.
					const property = propertyPath.slice(1); // Remove the leading dot
					value = get(fieldObject, property, '');
				} else {
					// No property specified, return the value
					value = fieldObject.value || '';
				}
			}
		} else if (dataSource) {
			value = getWithArrayExpansion(dataSource, key, '');
		}

		if ((value === '' || value === null || value === undefined) && settings) {
			value = get(settings, key, '');
		}
		if (typeof value !== 'string' || !matchHasQuotes) {
			return acc.replace(match, Array.isArray(value) ? `[${value.join(',')}]` : value);
		}
		return acc.replace(match, `"${value}"`);
	}, text);

	if (allMustMatch) {
		return matchesFound === matches.length ? textResult : null;
	}

	return textResult;
};

/**
 * Replaces {{key}} tags in the text with values from dataSource.
 * @param text The text containing tags like {{key}}
 * @param dataSource The data object to pull values from
 * @param settings The stored integration settings
 * @returns The text with tags replaced by values
 */
export function translateTags(
	text?: string,
	dataSource?: Record<string, any>,
	settings?: Record<string, any>,
	allMustMatch?: boolean
): string {
	return processTags(text, dataSource, settings, allMustMatch) as string;
}

/**
 * Recursively translates {{key}} tags in objects and strings with values from dataSource.
 * @param obj The object or string containing tags like {{key}}
 * @param dataSource The data object to pull values from
 * @param settings The stored integration settings
 * @returns The object/string with tags replaced by values
 */
export function translateTagsInObject(
	obj: any,
	dataSource?: Record<string, any>,
	allMustMatch?: boolean,
	settings?: Record<string, any>
): any {
	if (typeof obj === 'string') {
		return translateTags(obj, dataSource, settings);
	}

	if (Array.isArray(obj)) {
		return obj.map(item => translateTagsInObject(item, dataSource, allMustMatch, settings));
	}

	if (obj && typeof obj === 'object') {
		const result: any = {};
		for (const [key, value] of Object.entries(obj)) {
			result[key] = translateTagsInObject(value, dataSource, allMustMatch, settings);
		}
		return result;
	}

	return obj;
}

export function getObjectFromTranslatedMapping(translatedObject: any): any {
	if (typeof translatedObject === 'string') {
		// Add quotes around object keys to make it valid JSON
		const jsonString = translatedObject.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
		try {
			return JSON.parse(jsonString);
		} catch (error) {
			console.error('Failed to parse translated mapping:', error);
			return translatedObject;
		}
	}
	return translatedObject;
}

// Helper component to render interactive JSON as a pretty-printed tree with clickable keys/values
export const InteractiveJsonViewer = ({
	data,
	onPathClick,
	path = [],
	indent = 0,
	maxHeight = 350
}: {
	data: any;
	onPathClick?: (path: (string | number)[]) => void;
	path?: (string | number)[];
	indent?: number;
	maxHeight?: number;
}) => {
	const INDENT_SIZE = 2;
	const pad = (n: number) => ' '.repeat(n * INDENT_SIZE);

	const renderContent = () => {
		if (typeof data === 'object' && data !== null) {
			if (Array.isArray(data)) {
				return (
					<>
						{['[']}
						{data.map((item, idx) => (
							<span key={idx}>
								{'\n'}
								{pad(indent + 1)}
								<InteractiveJsonViewer
									data={item}
									onPathClick={onPathClick}
									path={[...path, idx]}
									indent={indent + 1}
									maxHeight={maxHeight}
								/>
								{idx < data.length - 1 ? ',' : ''}
							</span>
						))}
						{data.length > 0 ? '\n' + pad(indent) : ''}
						{[']']}
					</>
				);
			} else {
				const keys = Object.keys(data);
				return (
					<>
						{'{'}
						{keys.map((key, idx) => (
							<span key={key}>
								{'\n'}
								{pad(indent + 1)}
								<span
									className="UiWidgetHelpers__jsonKey"
									onClick={() => onPathClick?.([...path, key])}
								>
									{JSON.stringify(key)}
								</span>
								{': '}
								<InteractiveJsonViewer
									data={data[key]}
									onPathClick={onPathClick}
									path={[...path, key]}
									indent={indent + 1}
									maxHeight={maxHeight}
								/>
								{idx < keys.length - 1 ? ',' : ''}
							</span>
						))}
						{keys.length > 0 ? '\n' + pad(indent) : ''}
						{'}'}
					</>
				);
			}
		} else {
			return (
				<span className="UiWidgetHelpers__jsonValue" onClick={() => onPathClick?.(path)}>
					{JSON.stringify(data)}
				</span>
			);
		}
	};

	// Only wrap in pre tag at the root level (indent === 0)
	if (indent === 0) {
		return (
			<pre className="UiWidgetHelpers__jsonViewer" style={{ maxHeight: `${maxHeight}px`, overflowY: 'auto' }}>
				{renderContent()}
			</pre>
		);
	}

	return renderContent();
};
